import React from 'react';
import {
  X, MessageCircle, Video, Phone, Mic, Square, Volume2, Loader2, PhoneCall,
} from 'lucide-react';

const ChatTab = ({ chatMessages, chatInput, onChatInputChange, onSendMessage }) => (
  <div className="space-y-4">
    <div className="bg-[#1a1a1f] rounded-lg p-4 h-64 overflow-y-auto space-y-3">
      {chatMessages.map((msg, idx) => (
        <div
          key={`${msg.role}-${idx}-${(msg.content || '').slice(0, 12)}`}
          className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
        >
          <div
            className={`max-w-[80%] rounded-lg px-3 py-2 text-sm ${
              msg.role === 'user' ? 'bg-teal-500 text-white' : 'bg-gray-700 text-gray-200'
            }`}
          >
            {msg.content}
          </div>
        </div>
      ))}
    </div>
    <div className="flex gap-2">
      <input
        type="text"
        value={chatInput}
        onChange={(e) => onChatInputChange(e.target.value)}
        onKeyPress={(e) => e.key === 'Enter' && onSendMessage()}
        placeholder="Type a message..."
        className="flex-1 bg-[#1a1a1f] border border-gray-700 rounded-lg px-4 py-2 text-white placeholder-gray-500 outline-none focus:border-teal-400"
        data-testid="test-chat-input"
      />
      <button
        onClick={onSendMessage}
        className="px-4 py-2 bg-teal-500 text-white rounded-lg hover:bg-teal-400 transition-colors"
        data-testid="test-chat-send-btn"
      >
        Send
      </button>
    </div>
  </div>
);

const WebCallTab = ({
  webCallActive, isListening, isAISpeaking, webCallTranscript, currentUserSpeech,
  testMessage, onStartCall, onEndCall,
}) => {
  if (!webCallActive) {
    return (
      <div className="text-center py-6">
        <div className="w-20 h-20 bg-blue-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
          <Mic className="w-10 h-10 text-blue-400" />
        </div>
        <p className="text-gray-400 text-sm mb-4">
          Simulate a voice conversation with your AI agent using your microphone.
        </p>
        <button
          onClick={onStartCall}
          className="px-6 py-3 bg-green-500 text-white rounded-lg hover:bg-green-400 transition-colors flex items-center gap-2 mx-auto"
          data-testid="start-web-call-btn"
        >
          <Phone className="w-5 h-5" />
          Start Call
        </button>
        {testMessage && (
          <p className={`text-sm mt-4 ${testMessage.startsWith('❌') ? 'text-red-400' : 'text-gray-400'}`}>
            {testMessage}
          </p>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between bg-[#1a1a1f] rounded-lg p-3">
        <div className="flex items-center gap-3">
          <div className={`w-3 h-3 rounded-full ${
            isAISpeaking ? 'bg-teal-400 animate-pulse'
              : isListening ? 'bg-green-400 animate-pulse' : 'bg-gray-500'
          }`} />
          <span className="text-white text-sm">
            {isAISpeaking ? 'AI Speaking...' : isListening ? 'Listening...' : 'Processing...'}
          </span>
        </div>
        <button
          onClick={onEndCall}
          className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-400 transition-colors flex items-center gap-2"
          data-testid="end-web-call-btn"
        >
          <Square className="w-4 h-4" />
          End Call
        </button>
      </div>

      <div className="h-64 overflow-y-auto bg-[#1a1a1f] rounded-lg p-4 space-y-3">
        {webCallTranscript.map((msg, idx) => (
          <div
            key={`${msg.role}-${idx}-${(msg.content || '').slice(0, 12)}`}
            className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
          >
            <div className={`max-w-[80%] rounded-lg px-4 py-2 ${
              msg.role === 'user' ? 'bg-blue-500 text-white' : 'bg-teal-500/20 text-teal-300'
            }`}>
              <span className="text-xs opacity-70 block mb-1">
                {msg.role === 'user' ? 'You' : 'AI Agent'}
              </span>
              {msg.content}
            </div>
          </div>
        ))}
        {currentUserSpeech && (
          <div className="flex justify-end">
            <div className="max-w-[80%] rounded-lg px-4 py-2 bg-blue-500/50 text-white/70">
              <span className="text-xs opacity-70 block mb-1">You (speaking...)</span>
              {currentUserSpeech}
            </div>
          </div>
        )}
      </div>

      <div className="flex justify-center py-2">
        {isListening && (
          <div className="flex items-center gap-1">
            {[14, 22, 18, 26, 12].map((h, i) => (
              <div
                key={`mic-bar-${h}-${i}`}
                className="w-1 bg-green-400 rounded-full animate-pulse"
                style={{ height: `${h}px`, animationDelay: `${i * 0.1}s` }}
              />
            ))}
          </div>
        )}
        {isAISpeaking && (
          <div className="flex items-center gap-2">
            <Volume2 className="w-5 h-5 text-teal-400 animate-pulse" />
            <span className="text-teal-400 text-sm">AI is speaking...</span>
          </div>
        )}
      </div>
    </div>
  );
};

const PhoneCallTab = ({ testPhoneNumber, setTestPhoneNumber, testingCall, onInitiateCall, testMessage }) => (
  <div className="space-y-4">
    <p className="text-gray-400 text-sm">
      Enter your phone number to receive a test call from this AI agent.
    </p>
    <div className="space-y-3">
      <input
        type="tel"
        value={testPhoneNumber}
        onChange={(e) => setTestPhoneNumber(e.target.value)}
        placeholder="+1 (555) 123-4567"
        className="w-full bg-[#1a1a1f] border border-gray-700 rounded-lg px-4 py-3 text-white placeholder-gray-500 outline-none focus:border-green-400"
        data-testid="test-phone-input"
      />
      <button
        onClick={onInitiateCall}
        disabled={testingCall || !testPhoneNumber}
        className={`w-full flex items-center justify-center gap-2 py-3 rounded-lg font-medium transition-colors ${
          testingCall || !testPhoneNumber
            ? 'bg-gray-700 text-gray-500 cursor-not-allowed'
            : 'bg-green-500 text-white hover:bg-green-400'
        }`}
        data-testid="initiate-test-call-btn"
      >
        {testingCall ? (
          <><Loader2 className="w-4 h-4 animate-spin" /> Initiating Call...</>
        ) : (
          <><PhoneCall className="w-4 h-4" /> Call Me</>
        )}
      </button>
    </div>
    {testMessage && (
      <p className={`text-sm ${testMessage.startsWith('✅') ? 'text-green-400' : 'text-red-400'}`}>
        {testMessage}
      </p>
    )}
  </div>
);

const HEADER_ICONS = { chat: MessageCircle, web: Video, phone: Phone };
const HEADER_COLORS = { chat: 'text-teal-400', web: 'text-blue-400', phone: 'text-green-400' };
const HEADER_TITLES = { chat: 'Test Chat', web: 'Test Web Call', phone: 'Test Phone Call' };

export const TestAgentModal = ({
  testMode, onClose,
  // Chat tab
  chatMessages, chatInput, onChatInputChange, onSendMessage,
  // Web call tab
  webCallActive, isListening, isAISpeaking, webCallTranscript, currentUserSpeech,
  testMessage, onStartWebCall, onEndWebCall,
  // Phone call tab
  testPhoneNumber, setTestPhoneNumber, testingCall, onInitiateCall,
}) => {
  const Icon = HEADER_ICONS[testMode];
  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50" data-testid="test-agent-modal">
      <div className="bg-[#0d1117] border border-gray-800 rounded-xl w-full max-w-lg mx-4 shadow-2xl">
        <div className="flex items-center justify-between p-4 border-b border-gray-800">
          <div className="flex items-center gap-3">
            {Icon && <Icon className={`w-5 h-5 ${HEADER_COLORS[testMode]}`} />}
            <h3 className="text-white font-semibold">{HEADER_TITLES[testMode]}</h3>
          </div>
          <button
            onClick={onClose}
            className="p-1 text-gray-400 hover:text-white transition-colors"
            data-testid="test-modal-close-btn"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-4">
          {testMode === 'chat' && (
            <ChatTab
              chatMessages={chatMessages}
              chatInput={chatInput}
              onChatInputChange={onChatInputChange}
              onSendMessage={onSendMessage}
            />
          )}
          {testMode === 'web' && (
            <WebCallTab
              webCallActive={webCallActive}
              isListening={isListening}
              isAISpeaking={isAISpeaking}
              webCallTranscript={webCallTranscript}
              currentUserSpeech={currentUserSpeech}
              testMessage={testMessage}
              onStartCall={onStartWebCall}
              onEndCall={onEndWebCall}
            />
          )}
          {testMode === 'phone' && (
            <PhoneCallTab
              testPhoneNumber={testPhoneNumber}
              setTestPhoneNumber={setTestPhoneNumber}
              testingCall={testingCall}
              onInitiateCall={onInitiateCall}
              testMessage={testMessage}
            />
          )}
        </div>
      </div>
    </div>
  );
};
